import React, { useState, Fragment } from 'react'

import { FiltersSidebar } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { array, func, number, oneOfType, string, object } from 'prop-types'

import WithTriangleUiContext from 'Pods/Shared/Hocs/WithTriangleUiContext'
import i18n from 'Pods/Shared/I18n'

import getSelectedFilters from 'Utils/getSelectedFilters'
import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import PriceSlider from './Components/PriceSlider'
import FiltersWrapperWithTags from '../../FiltersWrapperWithTags/FiltersWrapperWithTags'

function Price({
  currentlySelectedFiltersObjects = EMPTY_ARRAY,
  currentSelectedFilters,
  initialFrom = null,
  initialTo = null,
  maxPriceThreshold,
  minPriceThreshold,
  setMultipleFilters,
  setSelectedFilters,
  rangeFilters,
  excludeFreeFilter,
  setShouldResetPriceRange,
  selectedFilters,
  setSearchEnchantmentAction,
  trackFilterChange,
  locale = 'en',
}) {
  const [triggerCloseDropdown, setTriggerCloseDropdown] = useState(false)

  const rangeLabel = i18n.t('listingPage.filters.quickSelections', { ns: 'rails', lng: locale })
  const pricePlaceholder = i18n.t('listingPage.filters.pricePlaceholder', { ns: 'rails', lng: locale })

  function removeFreeFilter() {
    const isFreeFilterActive = currentSelectedFilters.includes('free')
    if (isFreeFilterActive) {
      const newFiltersEntries = currentSelectedFilters
        .filter((value) => value && value !== 'free')
        .map((value) => [value, '1'])
      const newFilters = Object.fromEntries(newFiltersEntries)

      setMultipleFilters({ name: 'pricing', value: newFilters, skipFetch: true })
    }
  }

  function handleFilterChange(_, value, isOn, resetAll) {
    const selectedPricingFilters = getSelectedFilters(selectedFilters.pricing || [])
    const selectedPriceRangeFilters = selectedFilters.priceRange || []

    if (resetAll) {
      const newPricingFiltersEntries = selectedPricingFilters
        .filter((item) => item !== 'exclude_free')
        .map((item) => [item, '1'])
      const newPricingFilters = Object.fromEntries(newPricingFiltersEntries)
      window.removeCurrentSearchFilter({ name: 'pricing' })
      window.removeCurrentSearchFilter({ name: 'priceRange' })

      setSearchEnchantmentAction({
        type: 'remove_filter',
        value: [...selectedPriceRangeFilters, selectedPricingFilters.includes('exclude_free') && 'exclude_free']
          .filter(Boolean)
          .join(','),
      })
      setSelectedFilters({
        name: 'priceRange',
        value: [],
        skipFetch: true,
        skipGetLastSelectedFilter: true,
      })
      setSelectedFilters({
        name: 'pricing',
        value: newPricingFilters,
        skipGetLastSelectedFilter: true,
      })
      setShouldResetPriceRange(true)

      return
    }

    if (value === 'exclude_free') {
      const newPricingFiltersEntries = isOn
        ? [...selectedPricingFilters.filter((item) => item !== 'free'), value]
        : selectedPricingFilters.filter((item) => item !== 'exclude_free')
      const newPricingFilters = Object.fromEntries(newPricingFiltersEntries.map((item) => [item, '1']))
      setSelectedFilters({ name: 'pricing', value: newPricingFilters })

      if (isOn) {
        trackFilterChange({ name: 'pricing', value: 'exclude free' })
      } else {
        window.removeCurrentSearchFilter({ name: 'pricing', value: 'exclude free' })
      }

      return
    }

    if (value.includes('range_') || value.includes('more_than_')) {
      const newPriceRangeFilters = isOn
        ? [...selectedPriceRangeFilters, value]
        : selectedPriceRangeFilters.filter((item) => item !== value)
      removeFreeFilter()
      setSelectedFilters({ name: 'priceRange', value: newPriceRangeFilters })

      if (isOn) {
        trackFilterChange({ name: 'priceRange', value })
      } else {
        window.removeCurrentSearchFilter({ name: 'priceRange', value })
      }

      const rangeFiltersList = rangeFilters.map((item) => item.value)
      if (!rangeFiltersList.includes(value)) {
        setShouldResetPriceRange(true)
      }
    }
  }

  function handleEnterKeyPress() {
    setTriggerCloseDropdown(true)
  }

  function getContent() {
    return (
      <Fragment>
        <PriceSlider
          initialFrom={initialFrom}
          initialTo={initialTo}
          maxPriceThreshold={maxPriceThreshold}
          minPriceThreshold={minPriceThreshold}
          setSelectedFilters={setSelectedFilters}
          removeFreeFilter={removeFreeFilter}
          handleEnterKeyPress={handleEnterKeyPress}
        />
        <FiltersSidebar
          isCollapsible={false}
          filters={[{ filters: rangeFilters, label: rangeLabel }]}
          selectedFilters={currentSelectedFilters}
          onFiltersChange={handleFilterChange}
          isControlled
          useNewCheckboxes
          dividerClassName="listing-filters__divider"
          hideShowMore
        />
        <FiltersSidebar
          isCollapsible={false}
          filters={[{ filters: [excludeFreeFilter], label: null }]}
          selectedFilters={currentSelectedFilters}
          onFiltersChange={handleFilterChange}
          isControlled
          hideLastDivider
          useNewCheckboxes
          hideShowMore
        />
      </Fragment>
    )
  }

  return (
    <FiltersWrapperWithTags
      currentlySelectedFiltersObjects={currentlySelectedFiltersObjects}
      placeholder={pricePlaceholder}
      type="price"
      content={getContent()}
      handleFilterChange={handleFilterChange}
      triggerCloseDropdown={triggerCloseDropdown}
      setTriggerCloseDropdown={setTriggerCloseDropdown}
    />
  )
}

Price.propTypes = {
  currentlySelectedFiltersObjects: array,
  currentSelectedFilters: array.isRequired,
  initialFrom: oneOfType([string, number]),
  initialTo: oneOfType([string, number]),
  maxPriceThreshold: number.isRequired,
  minPriceThreshold: number.isRequired,
  setMultipleFilters: func.isRequired,
  setSelectedFilters: func.isRequired,
  rangeFilters: array.isRequired,
  excludeFreeFilter: object.isRequired,
  setShouldResetPriceRange: func.isRequired,
  selectedFilters: object.isRequired,
  setSearchEnchantmentAction: func.isRequired,
  trackFilterChange: func.isRequired,
  locale: string,
}

const mapStateToProps = ({
  maxPriceThreshold,
  minPriceThreshold,
  selectedFilters,
  setMultipleFilters,
  setSelectedFilters,
  filtersOptions,
  setShouldResetPriceRange,
  setSearchEnchantmentAction,
  trackFilterChange,
}) => {
  const rangeFilters = filtersOptions.priceRange.filters
  const rangeFiltersList = rangeFilters.map(({ value }) => value)
  const excludeFreeFilter = filtersOptions.pricing.filters.find(({ value }) => value === 'exclude_free')
  const selectedPricingFilters = selectedFilters.pricing || []
  const selectedPriceRangeFilters = selectedFilters.priceRange || []
  const customPriceRange = selectedPriceRangeFilters.find((value) => !rangeFiltersList.includes(value))
  const [initialFrom, initialTo] = (() => {
    if (customPriceRange) {
      const splitValue = customPriceRange.split('_').slice(1)
      return [splitValue[0], splitValue[1]]
    }

    return [undefined, undefined]
  })()
  const currentSelectedFilters = [...selectedPriceRangeFilters, ...getSelectedFilters(selectedPricingFilters)].filter(
    Boolean
  )
  const currentlySelectedFiltersObjects = getSelectedFiltersObjects(currentSelectedFilters)

  function getSelectedFiltersObjects(filters = []) {
    const selectedFiltersObjects = filters
      .map((filter) => [excludeFreeFilter, ...rangeFilters].find(({ value }) => value === filter))
      .filter(Boolean)

    if (initialFrom && initialTo) {
      selectedFiltersObjects.push({
        label: `$${initialFrom} - $${initialTo}`,
        value: `range_${initialFrom}_${initialTo}`,
      })
      currentSelectedFilters.push(`range_${initialFrom}_${initialTo}`)
    }

    return selectedFiltersObjects
  }

  return {
    currentlySelectedFiltersObjects,
    currentSelectedFilters,
    initialFrom,
    initialTo,
    maxPriceThreshold,
    minPriceThreshold,
    setMultipleFilters,
    setSelectedFilters,
    rangeFilters,
    excludeFreeFilter,
    setShouldResetPriceRange,
    selectedFilters,
    setSearchEnchantmentAction,
    trackFilterChange,
  }
}

export default WithTriangleUiContext(mapStateToProps)(Price)
